import { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import { Button, Card, Col, Descriptions, Row, Skeleton, Space, Table } from 'antd';
import styled from 'styled-components/macro';

import DrawerHandle from 'app/components/DrawerHandle';
import GojoSupporterAvatar from 'app/components/GojoSupporterAvatar';
import { PageLayout } from 'app/components/Layout/PageLayout.style';
import Detail from 'app/pages/AppPage/IndividualPage/components/Detail';
import userImage2 from 'styles/assets/icon_supporter_corporation.svg';
import userImage from 'styles/assets/icon_supporter.svg';
import { EXTRA_LIGHT_RED_COLOR, GRAY_COLOR, TEXT_GRAY_COLOR } from 'styles/StyleConstants';
import { checkGojoSupporter } from 'utils/supporterInfo';

import './Models/merge';

import Pagination from 'app/components/DecoupledPagination';
import HelpButton from 'app/components/HelpButton';
import {
  SupporterDetailData,
  SupporterType,
  Tab,
} from 'app/pages/AppPage/IndividualPage/utils/SupporterDetailData';
import useParseQueryParams from 'hook/useParseQueryParams';
import Alert from 'utils/alert';
import { ErrorHandler } from 'utils/ErrorHandler';

const supporterType = 1;

const StyledCandidateItem = styled.span`
  background: ${props => (props.shouldHighlight ? EXTRA_LIGHT_RED_COLOR : 'none')};
`;

const UserBox = styled.div`
  padding: 16px 24px;
  height: 100%;

  .title {
    font-size: 16px;
    font-weight: 600;
  }

  .ant-descriptions-bordered .ant-descriptions-view,
  .ant-descriptions-bordered .ant-descriptions-row {
    border: none;
  }

  .ant-descriptions-bordered .ant-descriptions-item-label {
    border: none;
    background: none;
    font-weight: 600;
    color: ${TEXT_GRAY_COLOR};
  }

  .ant-descriptions-bordered .ant-descriptions-item-label,
  .ant-descriptions-bordered .ant-descriptions-item-content {
    padding: 0;
  }

  .ant-descriptions-row:not(:last-of-type) {
    .ant-descriptions-item-label,
    .ant-descriptions-item-content {
      padding-bottom: 16px;
    }
  }
`;

const shouldHighlight = (item, attr, collection) => {
  if (collection && collection.length > 0) {
    const candidateMatchName = collection.filter(
      candidate => item[attr] && candidate[attr] === item[attr],
    );
    return candidateMatchName.length > 0;
  }
  return false;
};

const useGetMergeData = search => {
  const dispatch = useDispatch();
  const [isLoaded, setIsLoaded] = useState(null);
  const usp = new URLSearchParams(search);
  const query = Object.fromEntries(usp.entries());
  const step1Data = useSelector(state => state['@app//individual_merge_step1']);

  useEffect(() => {
    (async () => {
      try {
        const res = await dispatch['@app//individual_merge_step1'].get({
          page: query.page || 1,
          p1: query.p1 || '1',
        });
        res.status && setIsLoaded(true);
        window.scrollTo({
          top: 0,
        });
      } catch (error) {
        ErrorHandler.handle(error);
      }
    })();
  }, [JSON.stringify(query)]);

  const cancel = useCallback(async co_id => {
    try {
      await dispatch['@app//individual_merge_cancel'].proceed(co_id);
      await dispatch['@app//individual_merge_step1'].get({
        page: query.page || 1,
        p1: query.p1 || '1',
      });
      Alert.success('サポーターを名寄せ候補から除外しました。');
    } catch (error) {
      ErrorHandler.handle(error);
    }

    //dispatch(push(pathname));
  });
  return [step1Data, cancel, isLoaded];
};

const Candidate = ({ columns, item }) => {
  const { usp } = useParseQueryParams();
  const [selectedCoIdList, setSelectedCoIdList] = useState([]);

  const matchCoId = useMemo(() => {
    return selectedCoIdList.join(',');
  }, [selectedCoIdList]);

  usp.set('match_co_id', matchCoId);
  usp.set('co_id', item?.co_id);

  const rowSelection = {
    columnWidth: '48px',
    onChange: (selectedRowKeys, selectedRows) => {
      setSelectedCoIdList(selectedRowKeys);
    },
    getCheckboxProps: record => ({
      disabled: record.name === 'Disabled User',
      // Column configuration not to be checked
      name: record.name,
    }),
  };

  return (
    <>
      <Row className="py-4 px-6">
        <Col sm={24} md={20} lg={20}>
          <span style={{ fontSize: '16px', fontWeight: '600' }}>
            {'名寄せ条件に一致するサポーター'}
          </span>
        </Col>
      </Row>
      <Row>
        <Col sm={24} md={24} lg={24}>
          <Table
            className="clickable-table"
            dataSource={item?.一致}
            rowKey="co_id"
            columns={columns([item])}
            rowSelection={{
              ...rowSelection,
            }}
            pagination={false}
          />
        </Col>
      </Row>
      <Row className="py-4 px-6" justify="space-between" align="middle">
        <Col>
          <div>名寄せ候補と同一人物のサポーターを選択して名寄せを行なってください。</div>
        </Col>
        <Col>
          <Link className="sidebar-link" to={`/app/individuals-naming-detail?${usp}`}>
            <Button type="primary" disabled={!selectedCoIdList.length}>
              <span>{'名寄せ先の選択へ進む'}</span>
            </Button>
          </Link>
        </Col>
      </Row>
    </>
  );
};

const renderPageTitle = () => {
  return (
    <>
      <Helmet>
        <title>{'個人サポーターの名寄せ'}</title>
      </Helmet>
    </>
  );
};

const Naming = () => {
  const { search } = useParseQueryParams();
  const [step1Data, handleCancel, isLoaded] = useGetMergeData(search);

  // const options = [
  //   { label: 'メールアドレスが一致', value: '1' },
  //   { label: '氏名が一致', value: '2' },
  //   { label: '電話番号が一致', value: '3' },
  //   { label: '郵便番号が一致', value: '4' },
  // ];

  const columns = itemCollection => [
    {
      width: 120,
      title: 'サポーターNo.',
      render: row => (
        <StyledCandidateItem shouldHighlight={shouldHighlight(row, 'サポーターNo', itemCollection)}>
          {row.サポーターNo}
        </StyledCandidateItem>
      ),
    },
    {
      title: '氏名',
      render: row => {
        const isGojoSupporter = checkGojoSupporter(row.GOJO会員フラグ);

        if (isGojoSupporter) {
          return (
            <DrawerHandle drawerTitle={row.氏名 || '匿名'} drawerComponent={<Detail data={row} />}>
              <GojoSupporterAvatar
                gojoSupporterName={row.GOJO会員名}
                supporterName={row?.氏名}
                image={row.GOJO会員アイコン}
              />
            </DrawerHandle>
          );
        }

        return (
          <DrawerHandle drawerTitle={row.氏名 || '匿名'} drawerComponent={<Detail data={row} />}>
            <StyledCandidateItem
              className="supporter-link"
              shouldHighlight={shouldHighlight(row, '氏名', itemCollection)}
            >
              {row.氏名 || '匿名'}
            </StyledCandidateItem>
          </DrawerHandle>
        );
      },
    },
    {
      title: 'メールアドレス',
      render: row => (
        <StyledCandidateItem
          shouldHighlight={shouldHighlight(row, 'メールアドレス', itemCollection)}
        >
          {row.メールアドレス}
        </StyledCandidateItem>
      ),
    },
    {
      title: '電話番号',
      render: row => (
        <StyledCandidateItem shouldHighlight={shouldHighlight(row, '電話番号', itemCollection)}>
          {row.電話番号}
        </StyledCandidateItem>
      ),
    },
  ];

  return (
    <>
      {renderPageTitle()}
      <PageLayout>
        {/* タイトル・Title */}
        <Row type="flex" align="middle" className="mb-6">
          <Col className="mr-4">
            <div className="sub-page-title">{'個人サポーターの名寄せ'}</div>
          </Col>
          <HelpButton helpUrl="https://help.congrant.com/acd7a7fd9e8c44b8bb1f0a658bc13660?utm_source=cg_app&utm_medium=cg_app&utm_campaign=guide" />
        </Row>
        {isLoaded ? (
          <>
            {(step1Data.候補 || []).map(item => {
              const isGojoSupporter = checkGojoSupporter(item.GOJO会員フラグ);
              const supporterDetailData = new SupporterDetailData(
                SupporterType.Individuals,
                item.co_id,
                Tab.General,
              );
              return (
                <Card className="mb-6" bodyStyle={{ padding: 0 }}>
                  <Row>
                    <Col flex="0 1 340px" style={{ borderRight: `1px solid ${GRAY_COLOR}` }}>
                      <UserBox>
                        <div className="title mb-4">名寄せ候補</div>
                        <Row>
                          <Col span={24} className="mb-6">
                            {isGojoSupporter ? (
                              <GojoSupporterAvatar
                                type="detail"
                                gojoSupporterName={item.GOJO会員名}
                                supporterName={item?.氏名}
                                image={item.GOJO会員アイコン}
                                cgSupporterNumber={item.サポーターNo}
                              />
                            ) : (
                              <Space>
                                <div className="supporter-detail-image mr-4">
                                  <img src={supporterType === 1 ? userImage : userImage2} alt="" />
                                </div>
                                <Space direction="vertical" size={0}>
                                  <StyledCandidateItem
                                    style={{ fontSize: 13, fontWeight: 400 }}
                                    shouldHighlight={shouldHighlight(
                                      item,
                                      'サポーターNo',
                                      item?.一致,
                                    )}
                                  >
                                    {item.サポーターNo}
                                  </StyledCandidateItem>
                                  <strong style={{ fontSize: 16 }}>{item.氏名}</strong>
                                </Space>
                              </Space>
                            )}
                          </Col>
                          <Col span={24} className="mb-6">
                            <Descriptions column={1} bordered>
                              <Descriptions.Item label="氏名">
                                <DrawerHandle
                                  key={item.co_id}
                                  drawerTitle={item.氏名 || '匿名'}
                                  drawerComponent={<Detail data={supporterDetailData.toJSON()} />}
                                >
                                  <StyledCandidateItem
                                    className="supporter-link"
                                    shouldHighlight={shouldHighlight(item, '氏名', item?.一致)}
                                  >
                                    {item.氏名 || '匿名'}
                                  </StyledCandidateItem>
                                </DrawerHandle>
                              </Descriptions.Item>
                              <Descriptions.Item label="メールアドレス">
                                <StyledCandidateItem
                                  shouldHighlight={shouldHighlight(
                                    item,
                                    'メールアドレス',
                                    item?.一致,
                                  )}
                                >
                                  {item.メールアドレス}
                                </StyledCandidateItem>
                              </Descriptions.Item>
                              <Descriptions.Item label="電話番号">
                                <StyledCandidateItem
                                  shouldHighlight={shouldHighlight(item, '電話番号', item?.一致)}
                                >
                                  {item.電話番号 || '-'}
                                </StyledCandidateItem>
                              </Descriptions.Item>
                            </Descriptions>
                          </Col>
                          <Col>
                            <Button
                              onClick={() => {
                                handleCancel(item.co_id);
                              }}
                            >
                              このサポーターは名寄せしない
                            </Button>
                          </Col>
                        </Row>
                      </UserBox>
                    </Col>
                    <Col flex="auto" className="pb-6" style={{ width: 'calc(100% - 340px)' }}>
                      <Candidate columns={columns} item={item} />
                    </Col>
                  </Row>
                </Card>
              );
            })}
            {step1Data.pagination && (
              <Space className="ma-5" style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Pagination pagination={step1Data.pagination} />
              </Space>
            )}
          </>
        ) : (
          <Card>
            <Row>
              <Skeleton
                active
                avatar
                paragraph={{
                  rows: 4,
                }}
              />
            </Row>
          </Card>
        )}
      </PageLayout>
    </>
  );
};

export default Naming;
